import PropTypes from 'prop-types';
import React from 'react';
import { Form } from 'react-final-form';
import {
  BaseCard,
  BaseCardBody,
  BaseCardHeader,
  Button,
} from '@unite-us/ui';
import Notifier from 'common/helpers/Notifier';
import { useIsClientsDrawer } from '../../context/InteractionsContext';
import { computeQuantityValue, computeStartEnd } from '../../utils/computeStartEnd';
import { validateStructuredInteractionForm } from '../../utils/validateStructuredInteractionForm';
import { useCreateInteraction } from '../../hooks/useCreateInteraction';
import { useCreateProcedure } from '../../hooks/useCreateProcedure';
import { useInteractionConcepts } from '../../hooks/useInteractionConcepts';
import { PERIOD_TYPE } from '../../constants';
import { InteractionTypeRow } from './InteractionTypeRow';
import { PeriodOfServiceRow } from './PeriodOfServiceRow';
import { DurationRow } from './DurationRow';
import { NoteField } from './NoteField';

export function StructuredInteractionsForm({ onClose }) {
  const isClientsDrawer = useIsClientsDrawer();
  const {
    typeConcept,
    procedureOptions,
    isLoading,
  } = useInteractionConcepts();

  const createInteraction = useCreateInteraction();
  const createProcedure = useCreateProcedure();

  const onSubmit = async (values) => {
    try {
      const { start_time, end_time } = computeStartEnd(values);
      const quantity_value = computeQuantityValue(values);
      const interactionResponse = await createInteraction({
        note: values.note,
        start_time,
        end_time,
      });
      const interactionId = interactionResponse?.data?.data?.id;

      if (!interactionId) {
        throw new Error('Interaction was created but no id was returned.');
      }

      await createProcedure({
        interactionId,
        codeConceptId: values.codeConceptId,
        note: values.note,
        performed_start_time: start_time,
        performed_end_time: end_time,
        quantity_value,
      });

      Notifier.dispatch('success', 'Interaction saved.');
      onClose();
    } catch (error) {
      Notifier.handleErrors(error);
    }
  };

  return (
    <BaseCard
      className={isClientsDrawer ? 'mb-4' : 'mb-6'}
      data-test-element="structured-interactions-form"
    >
      <BaseCardHeader title="New Interaction" />
      <BaseCardBody withPadding>
        <Form
          initialValues={{ periodType: PERIOD_TYPE.SINGLE }}
          keepDirtyOnReinitialize
          onSubmit={onSubmit}
          validate={validateStructuredInteractionForm}
          render={({
            handleSubmit,
            form,
            values,
            hasValidationErrors,
            submitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="grid w-full grid-cols-1 gap-y-6">
                <div className="col-span-1 min-w-0 w-full">
                  <InteractionTypeRow
                    concept={typeConcept}
                    conceptLoading={isLoading}
                    procedureOptions={procedureOptions}
                    procedureLoading={isLoading}
                  />
                </div>
                <div className="col-span-1 min-w-0 w-full">
                  <PeriodOfServiceRow periodType={values.periodType} />
                </div>
                <div className="col-span-1 min-w-0 w-full">
                  <DurationRow />
                </div>
                <div className="col-span-1 min-w-0 w-full">
                  <NoteField />
                </div>
              </div>
              <div className="mt-6 grid auto-cols-max grid-flow-col justify-end gap-3">
                <Button
                  className="font-regular-font"
                  id="structured-interactions-cancel"
                  label="Cancel"
                  secondary
                  onClick={() => {
                    form.reset();
                    onClose();
                  }}
                  type="button"
                />
                <Button
                  className="font-regular-font"
                  disabled={submitting || hasValidationErrors || isLoading}
                  id="structured-interactions-save"
                  label="Save Interaction"
                  primary
                  type="submit"
                />
              </div>
            </form>
          )}
        />
      </BaseCardBody>
    </BaseCard>
  );
}

StructuredInteractionsForm.propTypes = {
  interaction: PropTypes.shape({
    id: PropTypes.string,
    class_concept: PropTypes.shape({ id: PropTypes.string }),
  }),
  onClose: PropTypes.func.isRequired,
};

StructuredInteractionsForm.defaultProps = {
  interaction: null,
};
